import {
	useCallback,
	useEffect,
	useRef,
	useReducer,
	useState
} from 'react';
import {
	useFormReducer,
	SET,
	UPDATE
} from '../reducers/useFormReducer';

const useForm = ( initialState = {} ) => {
	const [ state, dispatch ] = useReducer( useFormReducer, initialState );
	const [ hasChanged, setHasChanged ] = useState( false );

	// a reference to the initialState
	let initialData = useRef( initialState );

	// set initialData to state and set hasChanged to false
	const reinitializeFormData = useCallback( () => {
		initialData.current = state;
		setHasChanged( false );
	}, [ state ] );

	// set or overwrite the entire state object at once, reinitialize data unless false is passed as the second argument
	const setFormData = useCallback( ( data, reinitialize = true ) => {
		if ( reinitialize ) {
			initialData.current = data;
		}

		if ( data ) {
			SET( dispatch )( data );
		}
	}, [] );

	// set state to intialData
	const resetFormData = useCallback( () => {
		SET( dispatch )( initialData.current );
	}, [] );

	const compareData = useCallback( data => {
		// make sure the data is in the same order as the initialData
		const orderedData = {};

		for ( let key in initialData.current ) {
			orderedData[ key ] = data[ key ];
		}

		const areEqual = Object.keys( data ).length === Object.keys( initialData.current ).length
			? JSON.stringify( orderedData ) === JSON.stringify( initialData.current )
			: false;

		if ( areEqual ) {
			setHasChanged( false );
		} else {
			setHasChanged( true );
		}
	}, [] );

	useEffect( () => {
		compareData( state );
	}, [ compareData, state ] );

	// update a field in state
	const updateField = useCallback( ( { target }, reinitialize = false ) => {
		const {
			name,
			value
		} = target;

		if ( reinitialize ) {
			initialData.current = {
				...initialData.current,
				[ name ]: value
			};
		}

		UPDATE( dispatch )( {
			name,
			value
		} );
	}, [] );

	return {
		formData: state,
		hasChanged,
		initialData,
		updateField,
		setFormData,
		reinitializeFormData,
		resetFormData
	};
};

export default useForm;
